"use client";
/* eslint-disable @next/next/no-img-element */
// Navbar.tsx
import { ChevronDown, Menu, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useIsMobile";

import type {
  AllNavItems,
  CtaItem,
  DropdownItem,
  LinkItem,
  LinkItemOrDropdownItem,
  LogoItem,
} from "./navitems"; // adjust if your types live elsewhere

// ---------------- Render helpers ----------------

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="mr-2"
    >
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}

function LogoBlock({ logo }: { logo: LogoItem["darkAndLightLogoItem"] }) {
  return (
    <a
      href={logo.logoLight.href}
      className="flex items-center gap-2"
      aria-label={logo.logoLight.alt}
    >
      {/* Light mode logo */}
      <img src={logo.logoLight.src} alt={logo.logoLight.alt} className="h-8 w-auto dark:hidden" />
      {/* Dark mode logo */}
      <img
        src={logo.logoDark.src}
        alt={logo.logoDark.alt}
        className="h-8 w-auto hidden dark:block"
      />
    </a>
  );
}

function LinkLeaf({ item }: { item: LinkItem }) {
  return (
    <NavigationMenuLink asChild>
      <a href={item.link.href} className="px-3 py-2 text-sm font-medium hover:underline">
        {item.link.label}
      </a>
    </NavigationMenuLink>
  );
}

function DropdownDesktop({ item }: { item: DropdownItem }) {
  return (
    <>
      <NavigationMenuTrigger className="text-sm font-medium">
        {item.dropdown.label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <ul className="grid gap-1 p-3 w-64">
          {item.dropdown.items.map((sub, idx) => (
            <li key={idx}>
              <NavigationMenuLink asChild>
                <a
                  href={sub.link.href}
                  className="block rounded-md px-3 py-2 text-sm hover:bg-accent"
                >
                  {sub.link.label}
                </a>
              </NavigationMenuLink>
            </li>
          ))}
        </ul>
      </NavigationMenuContent>
    </>
  );
}

function CTAButtons({
  ctas,
  orientation = "row",
}: {
  ctas: CtaItem[];
  orientation?: "row" | "col";
}) {
  if (!ctas?.length) return null;
  return (
    <div className={orientation === "row" ? "flex items-center gap-2" : "flex flex-col gap-2 mt-4"}>
      {ctas.map((c, idx) => {
        const Icon = c.ctaButton.icon;
        return (
          <Button
            key={`${c.ctaButton.label}-${idx}`}
            asChild
            variant={c.ctaButton.href.startsWith("tel:") ? "secondary" : "default"}
            className={orientation === "col" ? "w-full justify-start" : ""}
          >
            <a href={c.ctaButton.href} aria-label={c.ctaButton.label}>
              <span className="inline-flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {c.ctaButton.label}
              </span>
            </a>
          </Button>
        );
      })}
    </div>
  );
}

// ---------------- Desktop & Mobile ----------------

function DesktopNav({
  logoItem,
  links,
  ctas,
  userMenu,
}: {
  logoItem: LogoItem;
  links: LinkItemOrDropdownItem[];
  ctas: CtaItem[];
  userMenu?: { displayName: string; links: LinkItem[] };
}) {
  return (
    <div className="hidden md:flex pt-10 pb-6 h-16 items-center justify-between px-4 lg:px-6 border-b bg-muted backdrop-blur">
      <LogoBlock logo={logoItem.darkAndLightLogoItem} />

      <NavigationMenu>
        <NavigationMenuList>
          {links.map((item, idx) => (
            <NavigationMenuItem key={idx}>
              {item.type === "link" ? (
                <LinkLeaf item={item} />
              ) : item.type === "dropdown" ? (
                <DropdownDesktop item={item} />
              ) : null}
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>

      <div className="flex items-center">
        <ThemeToggle />
        {userMenu ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="mr-2">
                <span className="inline-flex items-center gap-1">
                  {userMenu.displayName}
                  <ChevronDown className="h-4 w-4 opacity-70" />
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <ul className="flex flex-col">
                {userMenu.links.map((l, idx) => (
                  <li key={idx}>
                    <a
                      href={l.link.href}
                      className="block rounded-md px-3 py-2 text-sm hover:bg-accent"
                    >
                      {l.link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        ) : null}
        <CTAButtons ctas={ctas} orientation="row" />
      </div>
    </div>
  );
}

function MobileNav({
  logoItem,
  links,
  ctas,
  userMenu,
}: {
  logoItem: LogoItem;
  links: LinkItemOrDropdownItem[];
  ctas: CtaItem[];
  userMenu?: { displayName: string; links: LinkItem[] };
}) {
  return (
    <div className="md:hidden pt-10 pb-6 h-14 flex items-center justify-between px-3 border-b bg-muted backdrop-blur">
      <LogoBlock logo={logoItem.darkAndLightLogoItem} />

      <div className="flex items-center">
        <ThemeToggle />
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0">
            <SheetHeader className="px-4 py-3 text-left">
              <SheetTitle className="flex items-center gap-2">
                <LogoBlock logo={logoItem.darkAndLightLogoItem} />
              </SheetTitle>
            </SheetHeader>
            <Separator />

            <nav className="px-2 py-3">
              <ul className="flex flex-col">
                {links.map((item, idx) => (
                  <li key={idx}>
                    {item.type === "link" ? (
                      <a
                        href={item.link.href}
                        className="block rounded-md px-3 py-2 text-sm hover:bg-accent"
                      >
                        {item.link.label}
                      </a>
                    ) : item.type === "dropdown" ? (
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value={`dd-${idx}`}>
                          <AccordionTrigger className="px-3 text-sm">
                            {item.dropdown.label}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="pl-1">
                              {item.dropdown.items.map((sub, sIdx) => (
                                <li key={sIdx}>
                                  <a
                                    href={sub.link.href}
                                    className="block rounded-md px-3 py-2 text-sm hover:bg-accent"
                                  >
                                    {sub.link.label}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : null}
                  </li>
                ))}
              </ul>

              {userMenu ? (
                <div className="mt-4">
                  <div className="px-3 pb-2 text-xs uppercase text-muted-foreground">
                    {userMenu.displayName}
                  </div>
                  <ul className="flex flex-col">
                    {userMenu.links.map((l, idx) => (
                      <li key={idx}>
                        <a
                          href={l.link.href}
                          className="block rounded-md px-3 py-2 text-sm hover:bg-accent"
                        >
                          {l.link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <CTAButtons ctas={ctas} orientation="col" />
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

// ---------------- Main ----------------

export function Navbar({ items }: { items: AllNavItems }) {
  const isMobile = useIsMobile();
  return (
    <header className="sticky top-0 z-50 w-full">
      {isMobile ? (
        <MobileNav
          logoItem={items.logoItem}
          links={items.linkItems}
          ctas={items.ctaItems}
          userMenu={items.userMenu}
        />
      ) : (
        <DesktopNav
          logoItem={items.logoItem}
          links={items.linkItems}
          ctas={items.ctaItems}
          userMenu={items.userMenu}
        />
      )}
    </header>
  );
}
